import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { Menu } from '../types';
import ImageUpload from '../components/ImageUpload';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';

const MenuManagement: React.FC = () => {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  const [menuItems, setMenuItems] = useState<Menu[]>([]);
  const [filteredItems, setFilteredItems] = useState<Menu[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Menu | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [formData, setFormData] = useState({
    photo: '',
    name: '',
    description: '',
    category_id: '',
    is_available: true
  });

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, searchTerm, categoryFilter, statusFilter]);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('habib_menu_categories')
      .select('*')
      .order('name', { ascending: true });
    
    if (error) {
      console.error('Error fetching categories:', error);
    } else if (data) {
      setCategories(data);
    }
  };

  const filterItems = () => {
    let filtered = menuItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || 
                             item.category_id?.toString() === categoryFilter ||
                             item.category === categoryFilter; // Backward compatibility
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'available' ? item.is_available : !item.is_available);
      return matchesSearch && matchesCategory && matchesStatus;
    });
    setFilteredItems(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  const fetchMenuItems = async () => {
    const { data, error } = await supabase
      .from('habib_menu')
      .select(`
        *,
        menu_category:habib_menu_categories(id, name, description)
      `)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching menu:', error);
    } else if (data) {
      setMenuItems(data);
      setFilteredItems(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      photo: formData.photo,
      name: formData.name,
      description: formData.description,
      category_id: formData.category_id ? parseInt(formData.category_id) : null,
      is_available: formData.is_available
    };
    
    if (editingItem) {
      const { error } = await supabase
        .from('habib_menu')
        .update(submitData)
        .eq('id', editingItem.id);
      
      if (error) console.error('Error updating menu item:', error);
    } else {
      const { error } = await supabase
        .from('habib_menu')
        .insert([submitData]);
      
      if (error) console.error('Error creating menu item:', error);
    }
    
    fetchMenuItems();
    resetForm();
  };

  const handleEdit = (item: Menu) => {
    setEditingItem(item);
    setFormData({
      photo: item.photo,
      name: item.name,
      description: item.description,
      category_id: item.category_id?.toString() || '',
      is_available: item.is_available
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('areYouSureDeleteMenuItem'))) {
      const { error } = await supabase.from('habib_menu').delete().eq('id', id);
      if (error) console.error('Error deleting menu item:', error);
      fetchMenuItems();
    }
  };

  const toggleAvailability = async (item: Menu) => {
    const { error } = await supabase
      .from('habib_menu')
      .update({ is_available: !item.is_available })
      .eq('id', item.id);
    
    if (error) console.error('Error toggling availability:', error);
    fetchMenuItems();
  };

  const resetForm = () => {
    setFormData({
      photo: '',
      name: '',
      description: '',
      category_id: '',
      is_available: true
    });
    setEditingItem(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold" style={{color: '#05472A'}}>{t('menuFoodDrink')}</h2>
          <p className="text-sm sm:text-base" style={{color: '#05472A99'}}>{t('manageRestaurantMenu')}</p>
        </div>
        <button 
          onClick={() => setShowForm(true)} 
          className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95 flex items-center gap-2"
          style={{backgroundColor: '#05472A'}}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('addMenuItem')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-soft border p-4 sm:p-6 space-y-4" style={{borderColor: '#05472A20'}}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchMenuItems')}
              className="w-full px-4 py-3 pl-12 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            />
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            >
              <option value="all">{t('allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id.toString()}>
                  {cat.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
              style={{borderColor: '#05472A20'}}
            >
              <option value="all">{t('allStatus')}</option>
              <option value="available">{t('available')}</option>
              <option value="unavailable">{t('notAvailable')}</option>
            </select>
          </div>
        </div>
        <div className="text-sm" style={{color: '#05472A99'}}>
          {t('showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredItems.length)} {t('of')} {filteredItems.length} {t('items')}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 rounded-t-2xl" style={{borderColor: '#05472A20'}}>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold" style={{color: '#05472A'}}>
                  {editingItem ? t('editMenuItem') : t('addNewMenuItem')}
                </h3>
                <button 
                  onClick={resetForm}
                  className="p-2 rounded-xl hover:bg-green-50 transition-colors"
                  style={{color: '#05472A'}}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('itemImage')}</label>
                  <ImageUpload
                    onImageUploaded={(url) => setFormData({...formData, photo: url})}
                    currentImage={formData.photo}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('itemName')}</label>
                  <input
                    type="text"
                    placeholder={t('enterItemName')}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                    style={{borderColor: '#05472A20'}}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('description')}</label>
                  <textarea
                    placeholder={t('enterItemDescription')}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300 resize-none"
                    style={{borderColor: '#05472A20'}}
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('category')}</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300"
                    style={{borderColor: '#05472A20'}}
                    required
                  >
                    <option value="">{t('selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{color: '#05472A'}}>{t('stockStatus')}</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.is_available}
                        onChange={() => setFormData({...formData, is_available: true})}
                        className="w-4 h-4"
                        style={{accentColor: '#05472A'}}
                      />
                      <span className="text-sm font-medium" style={{color: '#05472A'}}>{t('available')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.is_available}
                        onChange={() => setFormData({...formData, is_available: false})}
                        className="w-4 h-4"
                        style={{accentColor: '#05472A'}}
                      />
                      <span className="text-sm font-medium text-gray-600">{t('notAvailable')}</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  <button 
                    type="submit" 
                    className="flex-1 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95"
                    style={{backgroundColor: '#05472A'}}
                  >
                    {editingItem ? t('updateItem') : t('createItem')}
                  </button>
                  <button 
                    type="button" 
                    onClick={resetForm} 
                    className="flex-1 bg-white hover:bg-green-50 border px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium"
                    style={{color: '#05472A', borderColor: '#05472A40'}}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Menu Items Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {currentItems.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden group hover:scale-105 border" style={{borderColor: '#05472A20'}}>
            <div className="relative overflow-hidden">
              <img 
                src={item.photo} 
                alt={item.name}
                className="w-full h-40 sm:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  item.is_available 
                    ? 'bg-green-100 border border-green-200' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`} style={item.is_available ? {color: '#05472A'} : {}}>
                  {item.is_available ? t('available') : t('notAvailable')}
                </span>
              </div>
              <div className="absolute top-3 left-3">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white border" style={{color: '#05472A', borderColor: '#05472A40'}}>
                  {item.menu_category?.name || item.category || t('noCategory')}
                </span>
              </div>
            </div>
            <div className="p-4 sm:p-5">
              <h3 className="font-bold mb-2 text-sm sm:text-base line-clamp-2" style={{color: '#05472A'}}>{item.name}</h3>
              <p className="text-xs sm:text-sm mb-4 line-clamp-2" style={{color: '#05472A99'}}>{item.description}</p>
              <div className="flex gap-2">
                <button 
                  onClick={() => toggleAvailability(item)}
                  className={`flex-1 px-3 py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm ${
                    item.is_available ? 'bg-yellow-500 text-white hover:bg-yellow-600' : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  {item.is_available ? t('markUnavailable') : t('markAvailable')}
                </button>
              </div>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={() => handleEdit(item)}
                  className="flex-1 bg-white hover:bg-green-50 border px-3 py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm flex items-center justify-center gap-2"
                  style={{color: '#05472A', borderColor: '#05472A40'}}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('edit')}
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-2xl shadow-soft border p-4" style={{borderColor: '#05472A20'}}>
          <div className="flex justify-between items-center">
            <p className="text-sm" style={{color: '#05472A99'}}>
              {t('page')} {currentPage} {t('of')} {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                ←
              </button>
              <span className="px-3 py-1 font-medium" style={{color: '#05472A'}}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50"
                style={{borderColor: '#05472A40', color: '#05472A'}}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredItems.length === 0 && (
        <div className="bg-white rounded-2xl shadow-soft p-8 text-center border" style={{borderColor: '#05472A20'}}>
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{color: '#05472A'}}>
            {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ? t('noItemsFound') : t('noMenuItemsYet')}
          </h3>
          <p style={{color: '#05472A99'}}>
            {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ? t('tryAdjustingFilters') : t('addFirstMenuItem')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
